/* eslint-disable @typescript-eslint/no-explicit-any */

import { isAxiosError } from 'axios';
import { ApiResponse, api } from '@/api/api';
import { QUESTION_BANK_URLS } from '@/api/url';

export interface GetQuestionBanksResponse {
  question_banks: QuestionBank[];
  total: number;
}

export const apiGetQuestionBanks = async (): Promise<ApiResponse<GetQuestionBanksResponse>> => {
  try {
    const response = await api.get<ApiResponse<GetQuestionBanksResponse>>(
      QUESTION_BANK_URLS.GET_ALL
    );
    return response.data;
  } catch (error: any) {
    if (isAxiosError(error)) {
      return error.response?.data;
    }
    throw error;
  }
};

export interface CreateQuestionPayload {
  category: string;
  job_title: string;
  description?: string;
  questions: QuestionBankQuestion[];
}

export const apiCreateQuestion = async ({
  payload
}: {
  payload: CreateQuestionPayload;
}): Promise<ApiResponse<QuestionBank>> => {
  try {
    const response = await api.post<ApiResponse<QuestionBank>>(QUESTION_BANK_URLS.CREATE, payload);
    return response.data;
  } catch (error: any) {
    if (isAxiosError(error)) {
      return error.response?.data;
    }
    throw error;
  }
};

export const apiGetQuestion = async ({
  questionId
}: {
  questionId: string;
}): Promise<ApiResponse<QuestionBank>> => {
  try {
    const response = await api.get<ApiResponse<QuestionBank>>(QUESTION_BANK_URLS.GET(questionId));
    return response.data;
  } catch (error: any) {
    if (isAxiosError(error)) {
      return error.response?.data;
    }
    throw error;
  }
};

export interface UpdateQuestionPayload {
  category?: string;
  job_title?: string;
  description?: string;
  questions?: QuestionBankQuestion[];
}

export const apiUpdateQuestion = async ({
  questionId,
  payload
}: {
  questionId: string;
  payload: UpdateQuestionPayload;
}): Promise<ApiResponse<void>> => {
  try {
    const response = await api.put<ApiResponse<void>>(
      QUESTION_BANK_URLS.UPDATE(questionId),
      payload
    );
    return response.data;
  } catch (error: any) {
    if (isAxiosError(error)) {
      return error.response?.data;
    }
    throw error;
  }
};

export const apiDeleteQuestion = async ({
  questionId
}: {
  questionId: string;
}): Promise<ApiResponse<void>> => {
  try {
    const response = await api.delete<ApiResponse<void>>(QUESTION_BANK_URLS.DELETE(questionId));
    return response.data;
  } catch (error: any) {
    if (isAxiosError(error)) {
      return error.response?.data;
    }
    throw error;
  }
};

export interface UpdateQuestionOrderPayload {
  order: number;
}

export const apiUpdateQuestionOrder = async ({
  questionId,
  payload
}: {
  questionId: string;
  payload: UpdateQuestionOrderPayload;
}): Promise<ApiResponse<void>> => {
  try {
    const response = await api.put<ApiResponse<void>>(
      QUESTION_BANK_URLS.UPDATE_ORDER(questionId),
      payload
    );
    return response.data;
  } catch (error: any) {
    if (isAxiosError(error)) {
      return error.response?.data;
    }
    throw error;
  }
};

export interface GetQuestionBankCategoriesResponse {
  categories: string[];
}

export const apiGetQuestionBankCategories = async (): Promise<
  ApiResponse<GetQuestionBankCategoriesResponse>
> => {
  try {
    const response = await api.get<ApiResponse<GetQuestionBankCategoriesResponse>>(
      QUESTION_BANK_URLS.GET_CATEGORIES
    );
    return response.data;
  } catch (error: any) {
    if (isAxiosError(error)) {
      return error.response?.data;
    }
    throw error;
  }
};

export interface UpdateCategoriesPayload {
  old_category: string;
  new_category: string;
}

export const apiUpdateCategories = async ({
  payload
}: {
  payload: UpdateCategoriesPayload;
}): Promise<ApiResponse<void>> => {
  try {
    const response = await api.put<ApiResponse<void>>(
      QUESTION_BANK_URLS.UPDATE_CATEGORIES,
      payload
    );
    return response.data;
  } catch (error: any) {
    if (isAxiosError(error)) {
      return error.response?.data;
    }
    throw error;
  }
};

export interface ExtractQuestionBankPayload {
  file: File;
}

export const apiExtractQuestionBank = async ({
  payload
}: {
  payload: ExtractQuestionBankPayload;
}): Promise<ApiResponse<QuestionBankExtractionData>> => {
  try {
    const formData = new FormData();
    formData.append('file', payload.file);

    const response = await api.post<ApiResponse<QuestionBankExtractionData>>(
      QUESTION_BANK_URLS.EXTRACT,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    );

    return response.data;
  } catch (error: any) {
    if (isAxiosError(error)) {
      return error.response?.data;
    }

    throw error;
  }
};
